import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { slugify } from 'utils/helpers';
import { MessageBox } from 'components/_new/MessageBox';
import { ErrorContainer } from 'components/_new/ErrorContainer/ErrorContainer';
import { Dialog, DialogBaseExtendProps } from 'components/_new/Dialog';
import { Loader } from 'components/_new/Loader';
import { Button } from 'components/_new/Button';
import { KnowledgeBaseArticlePresentationResult, KnowledgeBaseArticleContentItem } from 'types/KnowledgeBase';
import { useAuth } from 'contexts/authContext';
import { usePanel } from 'contexts/panelContext';
import { knowledgeBaseApi } from 'api/knowledgeBase';
import { ContentParserOptions } from 'utils/contentParser';
import { ArticlePresenterContent } from './ArticlePresenterContent';
import { useArticleRead } from './useArticleRead';

type ArticlePresenterDialogContentProps = {
	articleQuery: UseQueryResult<KnowledgeBaseArticlePresentationResult>;
	notFoundAction: () => void;
	disableRating?: boolean;
	contentParserOptions?: ContentParserOptions;
	onFlagChange?: () => void;
	readed?: boolean;
};

const ArticlePresenterDialogContent = ({
	articleQuery,
	disableRating = false,
	notFoundAction,
	contentParserOptions,
	onFlagChange,
	readed,
}: ArticlePresenterDialogContentProps) => {
	const { t } = useTranslation();

	if (articleQuery.isError) {
		return (
			<ErrorContainer
				errorMessage={t('knowledgeBase.errorOccurred')}
				action={() => {
					articleQuery.refetch();
				}}
				error={articleQuery.error}
				meta={['presentationDialog']}
			/>
		);
	}

	if (articleQuery.isSuccess && articleQuery?.data) {
		if (!articleQuery.data.articleContent) {
			if (!articleQuery.data.alternates.length) {
				return (
					<ErrorContainer
						errorMessage={t('knowledgeBase.errorNotFound')}
						actionName={t('actions.close')}
						action={() => {
							notFoundAction();
						}}
					/>
				);
			}

			if (articleQuery.data.alternates.length > 0) {
				return <MessageBox message={t('knowledgeBase.articleIsNotAvailableInYourLanguage')} />;
			}
		}

		if (articleQuery.data.article) {
			return (
				<ArticlePresenterContent
					article={articleQuery.data.article}
					articleContent={articleQuery.data.articleContent}
					options={{ disableRating }}
					contentParserOptions={contentParserOptions}
					onFlagChange={onFlagChange}
					readed={readed}
				/>
			);
		}
	}

	return null;
};

const ArticleNavigation = ({
	currentArticleId,
	sessionArticles,
	onNavigate,
}: {
	currentArticleId: number | null;
	sessionArticles: KnowledgeBaseArticleContentItem[];
	onNavigate: (articleId: number) => void;
}) => {
	const { t } = useTranslation();

	if (!currentArticleId || !sessionArticles || sessionArticles.length <= 1) {
		return null;
	}

	const currentIndex = sessionArticles.findIndex((article) => article.articleId === currentArticleId);
	const hasPrevious = currentIndex > 0;
	const hasNext = currentIndex < sessionArticles.length - 1;

	const handlePrevious = () => {
		if (hasPrevious) {
			onNavigate(sessionArticles[currentIndex - 1].articleId);
		}
	};

	const handleNext = () => {
		if (hasNext) {
			onNavigate(sessionArticles[currentIndex + 1].articleId);
		}
	};

	return (
		<div className="flex justify-content-between w-full mt-4 pt-3 border-top-1 border-300">
			<Button
				label={t('auth.buttons.prev')}
				icon="arrow-left"
				variant="primary-outlined"
				onClick={handlePrevious}
				disabled={!hasPrevious}
			/>
			<Button
				label={t('auth.buttons.next')}
				icon="arrow-right"
				iconPosition="right"
				variant="primary-outlined"
				onClick={handleNext}
				disabled={!hasNext}
			/>
		</div>
	);
};

type ArticlePresenterDialogProps = DialogBaseExtendProps & {
	articleId: number | null;
	disableRating?: boolean;
	contentParserOptions?: ContentParserOptions;
	sessionArticles?: KnowledgeBaseArticleContentItem[];
	onArticleChange?: (articleId: number) => void;
	onFlagChange?: () => void;
	knowledgeBaseLinkPrefix?: string;
};

export const ArticlePresenterDialog = ({
	articleId,
	disableRating = false,
	contentParserOptions,
	sessionArticles,
	onArticleChange,
	onFlagChange,
	knowledgeBaseLinkPrefix,
	...dialogProps
}: ArticlePresenterDialogProps) => {
	const { t } = useTranslation();
	const { currentUser } = useAuth();
	const { currentApplicationData } = usePanel();

	const [currentArticleId, setCurrentArticleId] = useState(articleId);

	useEffect(() => {
		setCurrentArticleId(articleId);
		return () => {
			setCurrentArticleId(null);
		};
	}, [articleId]);

	const handleArticleNavigation = (newArticleId: number) => {
		setCurrentArticleId(newArticleId);
		onArticleChange?.(newArticleId);
	};

	useEffect(() => {
		if (dialogContentRef.current) {
			dialogContentRef.current.scrollTop = 0;
		}
	}, [currentArticleId]);

	const articleQuery = useQuery(
		[
			'KNOWLEDGE_BASE_ARTICLE',
			{
				userId: Number(currentUser?.id),
				articleId: currentArticleId,
				language: currentUser?.language,
				articleLanguage: null,
				isProfessionalViewEnabled: currentUser?.isProfessionalViewEnabled,
			},
		],
		() =>
			knowledgeBaseApi.application.getArticleById(currentApplicationData.id, Number(currentArticleId), {
				lang: null,
			}),
		{
			enabled: Boolean(currentArticleId),
		}
	);

	const dialogContentRef = useRef<HTMLDivElement>(null);

	const { readed } = useArticleRead({
		enabled: Boolean(!articleQuery.isError && articleQuery.isSuccess && articleQuery?.data.article),
		scrollContainerRef: dialogContentRef,
		articleData: articleQuery?.data,
		onRead: onFlagChange,
	});

	const standaloneUrl = React.useMemo(() => {
		if (!currentArticleId || !articleQuery.data?.articleContent || !knowledgeBaseLinkPrefix) return null;
		const { slug } = articleQuery.data.articleContent;
		const slugPart = slug ? `-${slugify(slug)}` : '';
		const normalizedPrefix = knowledgeBaseLinkPrefix.startsWith('/')
			? knowledgeBaseLinkPrefix
			: `/${knowledgeBaseLinkPrefix}`;
		return `${window.location.origin}${normalizedPrefix}/article/${currentArticleId}${slugPart}`;
	}, [currentArticleId, articleQuery.data?.articleContent, knowledgeBaseLinkPrefix]);

	const handleOpenInNewWindow = () => {
		if (standaloneUrl) {
			window.open(standaloneUrl, '_blank');
		}
	};

	return (
		<Dialog
			title={articleQuery.data?.articleContent?.title || ''}
			size="lg"
			bodyClassName="flex flex-column gap-4"
			contentRef={dialogContentRef}
			headerRight={
				standaloneUrl ? (
					<Button
						label={t('table.openFullscreen')}
						icon="arrow-up-right-from-square"
						iconOnly
						onClick={handleOpenInNewWindow}
						variantSize="lg"
						variant="grey-text"
						rounded
						title={t('table.openFullscreen')}
					/>
				) : null
			}
			{...dialogProps}
		>
			{articleQuery.isLoading ? (
				<Loader />
			) : (
				<>
					<ArticlePresenterDialogContent
						articleQuery={articleQuery}
						disableRating={disableRating}
						notFoundAction={() => {
							dialogProps?.onHide?.();
						}}
						contentParserOptions={{
							...contentParserOptions,
							handlers: {
								...contentParserOptions?.handlers,
								onArticleLinkClick: (newArticleId: number) => {
									setCurrentArticleId(newArticleId);
								},
							},
							disableFormat: true,
						}}
						onFlagChange={onFlagChange}
						readed={readed}
					/>
					{sessionArticles && (
						<ArticleNavigation
							currentArticleId={currentArticleId}
							sessionArticles={sessionArticles}
							onNavigate={handleArticleNavigation}
						/>
					)}
				</>
			)}
		</Dialog>
	);
};
